
if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      // Do not query children here — they are not guaranteed to exist yet (custom elements upgrade timing).
      this.scrollObserver = null;
    }

    /**
     * Refresh DOM refs after connect — constructor runs before light-DOM children are parsed.
     */
    syncDomRefs() {
      this.sectionId = this.dataset.section;
      this.paginationType = this.getAttribute('data-type');
      this.productList = document.querySelector(`ul#product-grid[data-id="${this.sectionId}"]`);
      this.loadMoreButton = this.querySelector('.btn--load-more');
      this.progressEl = this.querySelector('[data-pagination-progress]');
      this.progressTemplate = this.dataset.progressTemplate || '';
      // Align with paginate.items (filtered total). collection.products_count can be larger when
      // storefront filters reduce the result set — then children/grid length never reached the old target.
      const totalFromDataset = parseInt(this.dataset.totalItems || '', 10);
      this.countItem =
        Number.isFinite(totalFromDataset) && totalFromDataset > 0
          ? totalFromDataset
          : parseInt(this.productList?.getAttribute('data-products-count') || '0', 10);
      this.currentPage = this.getCurrentPageFromUrl();
      this.nextPageUrl = this.sanitizeNextPageUrl(
        this.getAttribute('data-next-url') ||
          this.loadMoreButton?.dataset.url ||
          this.loadMoreButton?.getAttribute('href') ||
          ''
      );
    }

    /**
     * Liquid uses href="#" when there is no paginate.next; that string is truthy and would skip finalize.
     * Fragment-only URLs are not valid "load next page" targets.
     */
    sanitizeNextPageUrl(url) {
      if (url == null) return '';
      const s = String(url).trim();
      if (!s || s === '#') return '';
      const beforeHash = s.split('#')[0].trim();
      if (!beforeHash) return '';
      return s;
    }

    connectedCallback() {
      this.syncDomRefs();
      this.refreshProgressElements();

      // Click interception is handled by document capture (see bottom of file) so navigation is never missed.

      if (!this.productList || !this.loadMoreButton) return;

      if (this.paginationType === 'infinite-scroll') {
        this.setupInfiniteScroll();
      }

      this.restoreScrollState();
      this.updateShowingProgress();
      this.finalizeIfAllProductsLoaded();
      if (!this.nextPageUrl) {
        this.finalizePagination();
      }
    }

    setupInfiniteScroll() {
      this.scrollObserver?.disconnect();
      this.scrollObserver = new IntersectionObserver(
        entries => entries[0].isIntersecting && this.loadNextPage(),
        { threshold: [0, 1] }
      );
      this.scrollObserver.observe(this);
    }

    async loadNextPage() {
      if (this.hasAttribute('loading')) return;
      this.nextPageUrl = this.sanitizeNextPageUrl(this.nextPageUrl);
      if (!this.nextPageUrl) {
        this.finalizePagination();
        return;
      }

      try {
        this.setAttribute('loading', true);
        this.toggleLoadingSpinner(true);

        const response = await fetch(this.nextPageUrl, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Failed to fetch page: ${response.status}`);
        const html = await response.text();

        this.renderFetchedProducts(html, this.nextPageUrl);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        this.toggleLoadingSpinner(false);
        this.removeAttribute('loading');
      }
    }

    renderFetchedProducts(html, loadedUrl) {
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const newProductGrid = parsedDocument.getElementById('product-grid');

      if (!newProductGrid?.children.length) {
        this.finalizePagination();
        return;
      }

      // Appending only grows the list below the current rows; keep the same document scroll offset
      // so the viewport stays on the pre-click area and the Load more block moves down (often off-screen).
      // Avoid anchor + scrollBy — early layout reads can produce huge deltas and jump to the page bottom.
      const scrollYBefore = window.scrollY;

      const loadedPageNum = this.extractPageFromUrl(loadedUrl);
      if (loadedPageNum !== null) {
        newProductGrid
          .querySelectorAll(':scope > li.grid__item, :scope > li.promo-productlist')
          .forEach((li) => {
            li.setAttribute('data-grid-page', String(loadedPageNum));
          });
      }

      newProductGrid.querySelectorAll('.grid__item')
        .forEach(product => this.productList.appendChild(product));

      this.loadMoreButton?.blur();

      const lockScrollY = () => {
        window.scrollTo(0, scrollYBefore);
      };
      if (loadedPageNum !== null) {
        this.currentPage = loadedPageNum;
      }

      lockScrollY();
      requestAnimationFrame(() => {
        lockScrollY();
        requestAnimationFrame(() => {
          lockScrollY();
          window.paginationSyncCollectionUrlToScroll?.();
        });
      });

      // Read next URL from fetched markup instead of hand-building query strings
      this.updateNextPageUrl(parsedDocument);
      this.finalizeIfAllProductsLoaded();
      this.updateShowingProgress();
    }

    finalizeIfAllProductsLoaded() {
      if (!this.productList || !this.loadMoreButton) return;
      if (this.countItem < 1) return;
      const loaded = this.productList.querySelectorAll(':scope > li.grid__item').length;
      if (loaded >= this.countItem) {
        this.finalizePagination();
      }
    }

    updateShowingProgress() {
      if (!this.progressEl || !this.productList) return;

      const start = parseInt(this.dataset.showingStart, 10) || 1;
      const total = parseInt(this.dataset.totalItems, 10);
      const productRows = this.productList.querySelectorAll(':scope > li.grid__item').length;
      if (!productRows) return;

      const uncappedEnd = start + productRows - 1;
      const end = total > 0 ? Math.min(uncappedEnd, total) : uncappedEnd;

      if (this.progressTemplate) {
        this.progressEl.textContent = this.progressTemplate
          .replace(/__S__/g, String(start))
          .replace(/__E__/g, String(end))
          .replace(/__T__/g, String(total));
      } else {
        this.progressEl.textContent = `Showing ${start} - ${end} of ${total}`;
      }
    }

    /**
     * Re-resolve progress node after facet swaps replace inner HTML.
     */
    refreshProgressElements() {
      this.progressEl = this.querySelector('[data-pagination-progress]');
      this.progressTemplate = this.dataset.progressTemplate || '';
    }

    toggleLoadingSpinner(isLoading) {
      if (!this.loadMoreButton) return;

      const spinner = this.loadMoreButton.querySelector('.loading__spinner');
      this.loadMoreButton.classList.toggle('loading', isLoading);
      spinner?.classList.toggle('hidden', !isLoading);
    }

    finalizePagination() {
      this.scrollObserver?.unobserve(this);
      this.scrollObserver?.disconnect();
      this.nextPageUrl = '';
      this.setAttribute('data-next-url', '');
      if (this.loadMoreButton) {
        this.loadMoreButton.setAttribute('href', '#');
        this.loadMoreButton.dataset.url = '';
      }
      this.loadMoreButton?.classList.add('visually-hidden');
    }

    disconnectedCallback() {
      this.scrollObserver?.disconnect();
    }

    async restoreScrollState() {
      const savedPageRaw = sessionStorage.getItem('pagination-currentPage');
      const savedScrollRaw = sessionStorage.getItem('pagination-scrollY');
      if (savedPageRaw === null || savedScrollRaw === null) return;

      const savedPage = parseInt(savedPageRaw, 10);
      const savedScrollY = parseInt(savedScrollRaw, 10);
      if (Number.isNaN(savedPage) || savedPage < 1) return;

      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }

      const returnHref = sessionStorage.getItem('pagination-return-href');

      if (savedPage >= 2) {
        await this.loadPagesUpTo(savedPage);
      }

      sessionStorage.removeItem('pagination-currentPage');
      sessionStorage.removeItem('pagination-scrollY');
      sessionStorage.removeItem('pagination-return-href');

      const maxScrollY = () =>
        Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

      const applyScrollY = () => {
        if (!Number.isFinite(savedScrollY) || savedScrollY < 0) return;
        window.scrollTo(0, Math.min(savedScrollY, maxScrollY()));
      };

      const normalizePath = (hrefAttr) => {
        if (!hrefAttr) return '';
        try {
          const u = new URL(hrefAttr, window.location.origin);
          return u.pathname + u.search;
        } catch {
          return hrefAttr.split('#')[0] || '';
        }
      };

      const tryScrollToProductCard = () => {
        if (!returnHref) return false;
        const want = normalizePath(returnHref);
        if (!want) return false;
        const grid = document.getElementById('product-grid');
        if (!grid) return false;
        for (const a of grid.querySelectorAll('a.product-link[href]')) {
          const attr = a.getAttribute('href');
          if (!attr) continue;
          const got = normalizePath(attr);
          if (got === want || attr === returnHref) {
            const li = a.closest('li.grid__item');
            if (li) {
              li.scrollIntoView({ block: 'center', inline: 'nearest' });
              return true;
            }
          }
        }
        return false;
      };

      const tryRestorePosition = () => {
        if (!tryScrollToProductCard()) {
          applyScrollY();
        }
      };

      tryRestorePosition();
      requestAnimationFrame(() => {
        tryRestorePosition();
        requestAnimationFrame(() => {
          tryRestorePosition();
        });
      });
      window.setTimeout(tryRestorePosition, 50);
      window.setTimeout(tryRestorePosition, 150);
      window.setTimeout(tryRestorePosition, 400);
      window.setTimeout(tryRestorePosition, 800);
      window.addEventListener('pageshow', tryRestorePosition, { once: true });
      if (document.readyState !== 'complete') {
        window.addEventListener('load', tryRestorePosition, { once: true });
      }
    }

    async loadPagesUpTo(page) {
      // Strict < : when the URL already is ?page=N, currentPage is N and the SSR grid already
      // shows that page — we must not run loadNextPage() or we fetch N+1 (e.g. back from PDP on ?page=2).
      while (this.currentPage < page) {
        await this.loadNextPage();
      }
    }

    getCurrentPageFromUrl() {
      const url = new URL(window.location.href);
      const page = parseInt(url.searchParams.get('page'), 10);
      // Current visible page (treat missing page param as page 1)
      return Number.isNaN(page) || page < 1 ? 1 : page;
    }

    updateNextPageUrl(parsedDocument) {
      const fetchedPagination = parsedDocument.querySelector('custom-pagination');
      const fetchedButton = parsedDocument.querySelector('custom-pagination .btn--load-more');
      const raw =
        fetchedPagination?.getAttribute('data-next-url') ||
        fetchedButton?.dataset.url ||
        fetchedButton?.getAttribute('href') ||
        '';
      const nextUrlFromMarkup = this.sanitizeNextPageUrl(raw);

      this.nextPageUrl = nextUrlFromMarkup;
      this.setAttribute('data-next-url', nextUrlFromMarkup);

      if (this.loadMoreButton) {
        this.loadMoreButton.dataset.url = nextUrlFromMarkup;
        this.loadMoreButton.setAttribute('href', nextUrlFromMarkup || '#');
      }

      if (!nextUrlFromMarkup) {
        this.finalizePagination();
      }
    }

    extractPageFromUrl(urlString) {
      if (!urlString) return null;

      try {
        const url = new URL(urlString, window.location.origin);
        const page = parseInt(url.searchParams.get('page'), 10);
        return Number.isNaN(page) || page < 1 ? null : page;
      } catch (error) {
        return null;
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}

if (!customElements.get('custom-load-previous')) {
  class CustomLoadPrevious extends HTMLElement {
    syncDomRefs() {
      this.sectionId = this.dataset.section;
      this.productList = document.querySelector(`ul#product-grid[data-id="${this.sectionId}"]`);
      this.loadPreviousButton = this.querySelector('.btn--load-previous');
      this.previousUrl =
        this.getAttribute('data-previous-url') ||
        this.loadPreviousButton?.dataset?.url ||
        this.loadPreviousButton?.getAttribute('href') ||
        '';
    }

    connectedCallback() {
      this.syncDomRefs();
    }

    toggleLoadingSpinner(isLoading) {
      const btn = this.loadPreviousButton;
      if (!btn) return;
      const spinner = btn.querySelector('.loading__spinner');
      btn.classList.toggle('loading', isLoading);
      spinner?.classList.toggle('hidden', !isLoading);
    }

    async loadPreviousPage() {
      if (this.hasAttribute('loading')) return;
      this.syncDomRefs();
      if (!this.previousUrl || this.previousUrl === '#') {
        this.hideControl();
        return;
      }
      if (!this.productList) return;

      try {
        this.setAttribute('loading', true);
        this.toggleLoadingSpinner(true);

        const response = await fetch(this.previousUrl, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Failed to fetch page: ${response.status}`);
        const html = await response.text();

        this.renderPrependedProducts(html);
      } catch (error) {
        console.error('Error fetching previous page:', error);
      } finally {
        this.toggleLoadingSpinner(false);
        this.removeAttribute('loading');
      }
    }

    renderPrependedProducts(html) {
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const newProductGrid = parsedDocument.getElementById('product-grid');

      if (!newProductGrid?.children.length) return;

      const items = Array.from(newProductGrid.querySelectorAll(':scope > li.grid__item'));
      if (!items.length) return;

      let prepPageNum = null;
      try {
        const u = new URL(this.previousUrl, window.location.origin);
        const p = parseInt(u.searchParams.get('page'), 10);
        if (!Number.isNaN(p) && p >= 1) prepPageNum = p;
      } catch {
        /* ignore */
      }
      if (prepPageNum !== null) {
        items.forEach((li) => li.setAttribute('data-grid-page', String(prepPageNum)));
      }

      const scrollYBefore = window.scrollY;

      // When LOAD PREVIOUS (or the grid container top) is still in view, the user expects the
      // page chrome (header, collection title, filters) to stay fixed — do not scroll the window.
      // Scrolling down to "follow" the first grid row would hide that chrome.
      const loadPrevEl = document.querySelector(
        `custom-load-previous[data-section="${this.sectionId}"]`
      );
      const loadPrevRect = loadPrevEl?.getBoundingClientRect();
      const loadPrevInView =
        loadPrevRect &&
        loadPrevRect.bottom > 0 &&
        loadPrevRect.top < window.innerHeight;

      const gridContainer = document.getElementById('ProductGridContainer');
      const gridContainerTop = gridContainer?.getBoundingClientRect().top ?? 0;
      const chromeLikelyVisible =
        loadPrevInView || (gridContainerTop > -40 && gridContainerTop < window.innerHeight * 0.85);

      // Fallback scroll correction when we do adjust (measured before DOM changes).
      const gridBeforeFallback = this.productList.offsetHeight;
      const scrollBeforeFallback = scrollYBefore;

      // Anchor to the first product row of the current page (before prepend).
      const anchorEl = this.productList.querySelector(':scope > li.grid__item');
      const topBefore =
        anchorEl && !chromeLikelyVisible ? anchorEl.getBoundingClientRect().top : null;

      const fragment = document.createDocumentFragment();
      items.forEach((node) => fragment.appendChild(node));
      this.productList.insertBefore(fragment, this.productList.firstChild);

      const restoreScrollY = (y) => {
        window.scrollTo(0, y);
      };

      if (chromeLikelyVisible) {
        restoreScrollY(scrollYBefore);
        requestAnimationFrame(() => {
          restoreScrollY(scrollYBefore);
          requestAnimationFrame(() => restoreScrollY(scrollYBefore));
        });
      } else if (anchorEl && anchorEl.isConnected && topBefore !== null) {
        const topAfter = anchorEl.getBoundingClientRect().top;
        window.scrollBy(0, topAfter - topBefore);
      } else {
        const gridAfter = this.productList.offsetHeight;
        window.scrollTo(0, scrollBeforeFallback + (gridAfter - gridBeforeFallback));
      }

      this.adjustShowingStartAfterPrepend(items.length);
      this.refreshPreviousUrlFromFetched(parsedDocument);
      this.notifyPaginationProgressUpdate();

      requestAnimationFrame(() => {
        window.paginationSyncCollectionUrlToScroll?.();
      });
    }

    adjustShowingStartAfterPrepend(count) {
      const paginationRoot = document.querySelector(
        `custom-pagination[data-section="${this.sectionId}"]`
      );
      if (!paginationRoot || count < 1) return;
      const cur = parseInt(paginationRoot.dataset.showingStart, 10);
      if (Number.isNaN(cur)) return;
      paginationRoot.dataset.showingStart = String(Math.max(1, cur - count));
    }

    refreshPreviousUrlFromFetched(parsedDocument) {
      const fetched = parsedDocument.querySelector(
        `custom-load-previous[data-section="${this.sectionId}"]`
      );
      const nextPrev =
        fetched?.getAttribute('data-previous-url') ||
        fetched?.querySelector('.btn--load-previous')?.getAttribute('href') ||
        '';

      this.previousUrl = nextPrev;
      this.setAttribute('data-previous-url', nextPrev);

      if (this.loadPreviousButton) {
        if (nextPrev) {
          this.loadPreviousButton.dataset.url = nextPrev;
          this.loadPreviousButton.setAttribute('href', nextPrev);
          this.loadPreviousButton.classList.remove('visually-hidden');
          this.classList.remove('visually-hidden');
        } else {
          this.hideControl();
        }
      }
    }

    hideControl() {
      this.previousUrl = '';
      this.setAttribute('data-previous-url', '');
      if (this.loadPreviousButton) {
        this.loadPreviousButton.setAttribute('href', '#');
        this.loadPreviousButton.dataset.url = '';
        this.loadPreviousButton.classList.add('visually-hidden');
      }
      this.classList.add('visually-hidden');
    }

    notifyPaginationProgressUpdate() {
      const paginationRoot = document.querySelector(
        `custom-pagination[data-section="${this.sectionId}"]`
      );
      if (!paginationRoot) return;
      if (typeof paginationRoot.refreshProgressElements === 'function') {
        paginationRoot.refreshProgressElements();
      }
      if (typeof paginationRoot.updateShowingProgress === 'function') {
        paginationRoot.updateShowingProgress();
      }
    }
  }

  customElements.define('custom-load-previous', CustomLoadPrevious);
}

/**
 * Capture phase: record scroll + pagination page + product card link before page-transition /
 * instant.page handlers run, so return-from-PDP restore is reliable.
 */
(function bindCollectionProductNavCapture() {
  if (bindCollectionProductNavCapture.done) return;
  bindCollectionProductNavCapture.done = true;

  function readCollectionPaginationPageFromDom() {
    const cp = document.querySelector('#ProductGridContainer custom-pagination');
    try {
      if (cp && typeof customElements?.upgrade === 'function') {
        customElements.upgrade(cp);
      }
      if (cp && typeof cp.syncDomRefs === 'function') {
        cp.syncDomRefs();
      }
      if (cp && Number.isFinite(Number(cp.currentPage))) {
        return Number(cp.currentPage);
      }
    } catch (_) {
      /* ignore */
    }
    const url = new URL(window.location.href);
    const p = parseInt(url.searchParams.get('page'), 10);
    return Number.isNaN(p) || p < 1 ? 1 : p;
  }

  document.addEventListener(
    'click',
    (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (typeof event.button === 'number' && event.button !== 0) return;

      const a = event.target.closest?.('a.product-link');
      if (!a || !a.href) return;

      const gridWrap = document.getElementById('ProductGridContainer');
      if (!gridWrap || !gridWrap.contains(a)) return;
      if (!gridWrap.querySelector('custom-pagination')) return;

      try {
        sessionStorage.setItem('pagination-scrollY', String(window.scrollY));
        sessionStorage.setItem('pagination-currentPage', String(readCollectionPaginationPageFromDom()));
        const hrefAttr = a.getAttribute('href');
        if (hrefAttr) sessionStorage.setItem('pagination-return-href', hrefAttr);
      } catch (_) {
        /* ignore */
      }
    },
    true
  );
})();

/**
 * Document capture ensures we always prevent full-page navigation for Load more, even if:
 * - pagination.js ran after first paint, or
 * - another handler stops propagation before it reaches <custom-pagination>.
 */
(function bindCollectionLoadMoreDocumentCapture() {
  if (bindCollectionLoadMoreDocumentCapture.done) return;
  bindCollectionLoadMoreDocumentCapture.done = true;

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.('a.btn--load-more');
      if (!link) return;

      const gridWrap = document.getElementById('ProductGridContainer');
      if (!gridWrap || !gridWrap.contains(link)) return;

      const root = link.closest('custom-pagination');
      if (!root || root.getAttribute('data-type') !== 'load-more-button') return;

      event.preventDefault();
      event.stopPropagation();

      try {
        if (typeof customElements?.upgrade === 'function') {
          customElements.upgrade(root);
        }
      } catch (_) {
        /* ignore */
      }

      if (typeof root.syncDomRefs === 'function') {
        root.syncDomRefs();
      }

      if (typeof root.loadNextPage === 'function') {
        root.loadNextPage();
      }
    },
    true
  );
})();

(function bindCollectionLoadPreviousDocumentCapture() {
  if (bindCollectionLoadPreviousDocumentCapture.done) return;
  bindCollectionLoadPreviousDocumentCapture.done = true;

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.('a.btn--load-previous');
      if (!link) return;

      const gridWrap = document.getElementById('ProductGridContainer');
      if (!gridWrap || !gridWrap.contains(link)) return;

      const root = link.closest('custom-load-previous');
      if (!root) return;

      event.preventDefault();
      event.stopPropagation();

      try {
        if (typeof customElements?.upgrade === 'function') {
          customElements.upgrade(root);
        }
      } catch (_) {
        /* ignore */
      }

      if (typeof root.syncDomRefs === 'function') {
        root.syncDomRefs();
      }

      if (typeof root.loadPreviousPage === 'function') {
        root.loadPreviousPage();
      }
    },
    true
  );
})();

(function bindPaginationBackToTopCapture() {
  if (bindPaginationBackToTopCapture.done) return;
  bindPaginationBackToTopCapture.done = true;

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.('[data-pagination-back-to-top]');
      if (!link) return;

      const gridWrap = document.getElementById('ProductGridContainer');
      if (!gridWrap || !gridWrap.contains(link)) return;

      event.preventDefault();
      event.stopPropagation();

      const headerHeight =
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const titleEl = document.getElementById('collection-title');
      const targetEl = titleEl || document.getElementById('ProductGridContainer');
      if (!targetEl) return;

      const rect = targetEl.getBoundingClientRect();
      const top = rect.top + window.scrollY - headerHeight - 8;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    },
    true
  );
})();

/**
 * While multiple paginated chunks are in the DOM, keep ?page= aligned with the viewport using replaceState
 * (does not add history entries). Preserves other query params (filters, sort).
 */
(function initCollectionScrollUrlSync() {
  if (initCollectionScrollUrlSync.done) return;
  initCollectionScrollUrlSync.done = true;

  let ticking = false;
  let lastSyncedPage = null;

  function computeActivePage() {
    const grid = document.getElementById('product-grid');
    if (!grid) return null;
    const wrap = document.getElementById('ProductGridContainer');
    if (!wrap?.contains(grid)) return null;

    const cells = grid.querySelectorAll(':scope > li[data-grid-page]');
    if (!cells.length) return null;

    const refY = window.scrollY + Math.min(Math.max(window.innerHeight * 0.3, 120), 400);
    let bestPage = parseInt(cells[0].getAttribute('data-grid-page'), 10) || 1;
    let bestDist = Infinity;

    for (const cell of cells) {
      const p = parseInt(cell.getAttribute('data-grid-page'), 10);
      if (Number.isNaN(p)) continue;
      const r = cell.getBoundingClientRect();
      const top = r.top + window.scrollY;
      const bottom = r.bottom + window.scrollY;
      if (refY >= top && refY <= bottom) return p;
      const dist = refY < top ? top - refY : refY - bottom;
      if (dist < bestDist) {
        bestDist = dist;
        bestPage = p;
      }
    }
    return bestPage;
  }

  function syncUrlToScroll() {
    const page = computeActivePage();
    if (page === null || page < 1) return;

    const url = new URL(window.location.href);
    const curParam = url.searchParams.get('page');
    const curNum = curParam == null ? 1 : parseInt(curParam, 10);
    const effectiveCur = Number.isNaN(curNum) ? 1 : curNum;

    if (page === effectiveCur && lastSyncedPage === page) return;
    lastSyncedPage = page;

    if (page <= 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', String(page));
    }
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state || {}, '', next);

    const cp = document.querySelector('custom-pagination');
    if (cp) {
      cp.currentPage = page;
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      syncUrlToScroll();
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);

  window.paginationSyncCollectionUrlToScroll = () => {
    lastSyncedPage = null;
    syncUrlToScroll();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      lastSyncedPage = null;
      syncUrlToScroll();
    });
  } else {
    lastSyncedPage = null;
    requestAnimationFrame(syncUrlToScroll);
  }
})();